import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fleetClient,
  FleetApiError,
  type AgentMode,
  type FleetAgent,
  type FleetView as FleetViewData,
  type Projections,
} from '@/fleet/fleetClient'

/**
 * Agent-driven load controls (live / replay / idle).
 * Shared by the unified Amp Load panel — formerly the Fleet page.
 */

const POLL_MS = 5_000

const fmt = (n: number | null | undefined, digits = 0) =>
  n == null || !Number.isFinite(n)
    ? '—'
    : Number(n).toLocaleString(undefined, { maximumFractionDigits: digits })

const mb = (bytes: number | null | undefined) =>
  bytes == null ? '—' : `${fmt(bytes / 1048576)} MB`

const SCENARIO_OPTS = [
  { id: 'baseline', label: 'Baseline' },
  { id: 'edge_rtt_spike', label: 'Edge RTT spike' },
  { id: 'demux_starve', label: 'Demux starve' },
  { id: 'decode_lag', label: 'Decode lag' },
  { id: 'render_drop', label: 'Render drop' },
  { id: 'bitrate_collapse', label: 'Bitrate collapse' },
  { id: 'incident_sim', label: 'Incident sim' },
]

export function FleetLoadSection({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<FleetViewData | null>(null)
  const [projections, setProjections] = useState<Projections | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [mode, setMode] = useState<AgentMode>('live')
  const [target, setTarget] = useState(10)
  const [vmsWsUrl, setVmsWsUrl] = useState('')
  const [datasetId, setDatasetId] = useState('')
  const [scenarioIds, setScenarioIds] = useState<string[]>(['baseline'])
  const [seed, setSeed] = useState<number | ''>('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const editingRef = useRef(false)

  const load = useCallback(async () => {
    try {
      const view = await fleetClient.getFleet()
      setData(view)
      setError(null)
      if (!vmsWsUrl && !editingRef.current) {
        const seedUrl = view.agents.find((a) => a.desired.vmsWsUrl)?.desired.vmsWsUrl
        if (seedUrl) setVmsWsUrl(seedUrl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load fleet')
    }
  }, [vmsWsUrl])

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), POLL_MS)
    return () => clearInterval(t)
  }, [load])

  const loadProjections = useCallback(async () => {
    try {
      setProjections(await fleetClient.getProjections(target, mode))
    } catch {
      setProjections(null)
    }
  }, [target, mode])

  useEffect(() => {
    void loadProjections()
  }, [loadProjections])

  async function applyFleetTarget() {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const body = {
        mode,
        clientCount: target,
        vmsWsUrl,
        ...(mode === 'replay'
          ? {
              ...(datasetId.trim() ? { datasetId: datasetId.trim() } : {}),
              scenarioIds: scenarioIds.length ? scenarioIds : ['baseline'],
              ...(seed !== '' ? { seed: Number(seed) } : {}),
            }
          : {}),
      }
      const r = await fleetClient.setFleetTarget(body)
      const undelivered = r.agents.filter((a) => !a.delivered)
      setNotice(
        `Allocated ${r.allocated} of ${r.requested} across ${r.agents.length} agent(s).` +
          (undelivered.length
            ? ` Not delivered to: ${undelivered.map((a) => a.agentId).join(', ')}.`
            : ''),
      )
      await load()
    } catch (err) {
      setError(err instanceof FleetApiError ? err.message : 'failed to set fleet target')
    } finally {
      setBusy(false)
    }
  }

  async function stopAll() {
    if (!window.confirm('Stop all agents? Any live or replay load will end.')) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fleetClient.setFleetTarget({ mode: 'idle', clientCount: 0, vmsWsUrl })
      setNotice('Fleet stopped.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to stop fleet')
    } finally {
      setBusy(false)
    }
  }

  async function setAgent(agent: FleetAgent, clientCount: number) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fleetClient.setAgentState(agent.agentId, {
        mode: clientCount === 0 ? 'idle' : mode,
        clientCount,
        vmsWsUrl: vmsWsUrl || agent.desired.vmsWsUrl,
        ...(mode === 'replay'
          ? {
              ...(datasetId.trim() ? { datasetId: datasetId.trim() } : {}),
              scenarioIds: scenarioIds.length ? scenarioIds : ['baseline'],
              ...(seed !== '' ? { seed: Number(seed) } : {}),
            }
          : {}),
      })
      setNotice(`${agent.agentId} set to ${clientCount} client(s).`)
      await load()
    } catch (err) {
      setError(err instanceof FleetApiError ? err.message : 'failed to set agent state')
    } finally {
      setBusy(false)
      editingRef.current = false
    }
  }

  const toggleScenario = (id: string) => {
    setScenarioIds((prev) => {
      if (id === 'baseline') return ['baseline']
      const next = prev.filter((x) => x !== 'baseline')
      return next.includes(id) ? next.filter((x) => x !== id) : [...next, id]
    })
  }

  const totals = data?.totals
  const capacityForMode =
    mode === 'live' ? (totals?.liveCapacity ?? 0) : totals?.replayCapacity ?? 0
  const overCapacity = target > capacityForMode && mode !== 'idle'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 14 }}>
      {error && <div style={bannerError}>{error}</div>}
      {notice && <div style={bannerOk}>{notice}</div>}

      <div style={{ fontSize: 10, color: 'var(--t-2)' }}>
        {totals
          ? `${totals.connected}/${totals.agents} agents · ${fmt(totals.actualClients)} running / ${fmt(totals.desiredClients)} desired`
          : 'loading agents…'}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
        <label style={field}>
          <span style={label}>Mode</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as AgentMode)}
            style={input}
          >
            <option value="live">live</option>
            <option value="replay">replay</option>
            <option value="idle">idle</option>
          </select>
        </label>
        <label style={field}>
          <span style={label}>Target clients</span>
          <input
            type="number"
            min={0}
            value={target}
            onFocus={() => {
              editingRef.current = true
            }}
            onBlur={() => {
              editingRef.current = false
            }}
            onChange={(e) => setTarget(Math.max(0, Number(e.target.value) || 0))}
            style={{ ...input, ...(overCapacity ? inputWarn : null) }}
          />
        </label>
        <div style={{ fontSize: 10, color: 'var(--t-2)', paddingBottom: 6 }}>
          of <strong>{fmt(capacityForMode)}</strong> {mode} capacity
          {overCapacity && (
            <div style={{ color: '#f0c674' }}>
              Over by {fmt(target - capacityForMode)} — will reject
            </div>
          )}
        </div>
      </div>

      <label style={{ ...field, width: '100%' }}>
        <span style={label}>VMS ingest URL</span>
        <input
          value={vmsWsUrl}
          placeholder="wss://host/ingest"
          onFocus={() => {
            editingRef.current = true
          }}
          onBlur={() => {
            editingRef.current = false
          }}
          onChange={(e) => setVmsWsUrl(e.target.value)}
          style={{ ...input, width: '100%', boxSizing: 'border-box' }}
        />
      </label>

      {mode === 'replay' && (
        <>
          <label style={{ ...field, width: '100%' }}>
            <span style={label}>Dataset id (optional)</span>
            <input
              value={datasetId}
              placeholder="pack id / filter"
              onChange={(e) => setDatasetId(e.target.value)}
              style={{ ...input, width: '100%', boxSizing: 'border-box' }}
            />
          </label>
          <label style={field}>
            <span style={label}>Seed (optional)</span>
            <input
              type="number"
              value={seed}
              onChange={(e) =>
                setSeed(e.target.value === '' ? '' : Number(e.target.value) || 0)
              }
              style={input}
            />
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 90, overflowY: 'auto' }}>
            {SCENARIO_OPTS.map((s) => (
              <label key={s.id} style={{ fontSize: 10, display: 'flex', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={scenarioIds.includes(s.id)}
                  onChange={() => toggleScenario(s.id)}
                />
                {s.label}
              </label>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => void applyFleetTarget()} disabled={busy} style={btnPrimary}>
          Apply to fleet
        </button>
        <button type="button" onClick={() => void stopAll()} disabled={busy} style={btnDanger}>
          Stop all
        </button>
      </div>

      <div style={{ maxHeight: compact ? 160 : 220, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={th}>Agent</th>
              <th style={th}>Mode</th>
              <th style={{ ...th, textAlign: 'right' }}>Clients</th>
              <th style={th}>Host</th>
            </tr>
          </thead>
          <tbody>
            {(data?.agents ?? []).map((a) => (
              <AgentMiniRow
                key={a.agentId}
                agent={a}
                mode={mode}
                busy={busy}
                expanded={expanded === a.agentId}
                onToggle={() => setExpanded(expanded === a.agentId ? null : a.agentId)}
                onSet={(n) => void setAgent(a, n)}
                onEditing={(v) => {
                  editingRef.current = v
                }}
              />
            ))}
            {data && data.agents.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 12, textAlign: 'center', color: 'var(--t-2)', fontSize: 11 }}>
                  No agents connected. Check AGENT_TOKEN / controller.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {projections && !compact && (
        <ProjectionsMini projections={projections} target={target} />
      )}
    </div>
  )
}

function AgentMiniRow({
  agent,
  mode,
  busy,
  expanded,
  onToggle,
  onSet,
  onEditing,
}: {
  agent: FleetAgent
  mode: AgentMode
  busy: boolean
  expanded: boolean
  onToggle: () => void
  onSet: (n: number) => void
  onEditing: (v: boolean) => void
}) {
  const [draft, setDraft] = useState(String(agent.desired.clientCount))
  const [captureBusy, setCaptureBusy] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [capturePath, setCapturePath] = useState<string | null>(null)
  const [captureError, setCaptureError] = useState<string | null>(null)

  useEffect(() => {
    setDraft(String(agent.desired.clientCount))
  }, [agent.desired.clientCount])

  const ceiling = mode === 'live' ? agent.capacity.maxLiveSessions : agent.capacity.maxClients
  const over = Number(draft) > ceiling
  const s = agent.actual.summary
  const host = agent.host

  async function startCapture() {
    setCaptureBusy(true)
    setCaptureError(null)
    try {
      const r = await fleetClient.startCapture(agent.agentId)
      if (r.error || r.ok === false) {
        setCaptureError(r.error || 'capture start failed')
        return
      }
      setCapturing(Boolean(r.capturing ?? r.ok ?? r.delivered))
      if (r.path) setCapturePath(r.path)
    } catch (err) {
      setCaptureError(err instanceof Error ? err.message : 'capture start failed')
    } finally {
      setCaptureBusy(false)
    }
  }

  async function stopCapture() {
    setCaptureBusy(true)
    setCaptureError(null)
    try {
      const r = await fleetClient.stopCapture(agent.agentId)
      if (r.error || r.ok === false) {
        setCaptureError(r.error || 'capture stop failed')
        return
      }
      setCapturing(false)
      if (r.path) setCapturePath(r.path)
    } catch (err) {
      setCaptureError(err instanceof Error ? err.message : 'capture stop failed')
    } finally {
      setCaptureBusy(false)
    }
  }

  return (
    <>
      <tr style={{ opacity: agent.connected ? 1 : 0.55 }}>
        <td style={td}>
          <button type="button" onClick={onToggle} style={{ border: 'none', background: 'transparent', color: 'var(--t-0)', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 11 }}>
            {expanded ? '▾' : '▸'} {agent.agentId}
          </button>
          <div style={{ fontSize: 9, color: 'var(--t-2)' }}>{agent.hostname}</div>
        </td>
        <td style={td}>{agent.actual.mode ?? '—'}</td>
        <td style={{ ...td, textAlign: 'right' }}>
          <input
            type="number"
            min={0}
            value={draft}
            disabled={busy || !agent.connected}
            onFocus={() => onEditing(true)}
            onBlur={() => onEditing(false)}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSet(Number(draft) || 0)
            }}
            style={{
              width: 56,
              fontSize: 11,
              padding: '2px 4px',
              borderRadius: 4,
              border: `0.5px solid ${over ? '#d8a13a' : 'var(--bd-0)'}`,
              background: 'var(--bg-2)',
              color: 'var(--t-0)',
              textAlign: 'right',
            }}
          />
          <span style={{ color: 'var(--t-2)', marginLeft: 4 }}>/ {fmt(agent.actual.clientCount)}</span>
          {!agent.converged && agent.connected && (
            <div style={{ fontSize: 9, color: '#f0c674' }}>drift</div>
          )}
        </td>
        <td style={td}>
          {host ? (
            <span style={{ fontSize: 10 }}>
              {fmt(host.cpuPct, 0)}% · {fmt(host.memUsedPct, 0)}%
            </span>
          ) : (
            '—'
          )}
          {agent.connected ? (
            <div style={{ fontSize: 9, color: 'var(--t-2)' }}>
              {s.playing > 0 ? `${s.playing} playing` : s.total === 0 ? 'idle' : `${s.total} sess`}
            </div>
          ) : (
            <div style={{ fontSize: 9, color: '#ff9aa2' }}>offline</div>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} style={{ ...td, background: 'var(--bg-2)', fontSize: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ color: 'var(--t-1)', lineHeight: 1.5 }}>
                {host ? (
                  <>
                    CPU {fmt(host.cpuPct, 0)}% · RAM {mb(host.memUsedBytes)} / {mb(host.memTotalBytes)}{' '}
                    ({fmt(host.memUsedPct, 0)}%)
                    {host.vramTotalBytes != null && host.vramTotalBytes > 0 && (
                      <>
                        {' '}
                        · VRAM {mb(host.vramUsedBytes)} / {mb(host.vramTotalBytes)}
                        {host.vramUsedPct != null ? ` (${fmt(host.vramUsedPct, 0)}%)` : ''}
                      </>
                    )}
                  </>
                ) : (
                  'No host sample yet'
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={captureBusy || !agent.connected || capturing}
                  onClick={() => void startCapture()}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: 5,
                    border: 'none',
                    background: 'var(--ok)',
                    color: '#fff',
                    cursor: 'pointer',
                    opacity: captureBusy || !agent.connected || capturing ? 0.5 : 1,
                  }}
                >
                  Start capture
                </button>
                <button
                  type="button"
                  disabled={captureBusy || !agent.connected || !capturing}
                  onClick={() => void stopCapture()}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '4px 8px',
                    borderRadius: 5,
                    border: '0.5px solid var(--bd-0)',
                    background: 'transparent',
                    color: 'var(--t-0)',
                    cursor: 'pointer',
                    opacity: captureBusy || !agent.connected || !capturing ? 0.5 : 1,
                  }}
                >
                  Stop capture
                </button>
                {capturing && (
                  <span style={{ fontSize: 9, color: '#f0c674' }}>capturing</span>
                )}
              </div>
              {capturePath && (
                <div style={{ fontSize: 9, color: 'var(--t-2)', wordBreak: 'break-all' }}>
                  {capturePath}
                </div>
              )}
              {captureError && (
                <div style={{ fontSize: 9, color: '#ff9aa2' }}>{captureError}</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function ProjectionsMini({ projections, target }: { projections: Projections; target: number }) {
  const { capacity, throughput, storage } = projections
  if (storage.basis.confidence === 'insufficient-data') {
    return (
      <p style={{ fontSize: 10, color: 'var(--t-2)', margin: 0 }}>
        Projections need pipeline volume samples — run load first.
      </p>
    )
  }
  return (
    <div style={{ fontSize: 10, color: 'var(--t-2)', lineHeight: 1.5 }}>
      <div style={{ fontWeight: 600, color: 'var(--t-1)', marginBottom: 4 }}>
        Projections @ {fmt(target)} ({storage.basis.confidence})
      </div>
      <div>
        msgs/s {fmt(throughput.messagesPerSec)} · Kafka{' '}
        {fmt((throughput.bytesPerSecToKafka ?? 0) / 1048576, 2)} MB/s
      </div>
      <div>
        storage/day {fmt(storage.gbPerDayLow, 1)}–{fmt(storage.gbPerDayHigh, 1)} GB
        {capacity.shortfall != null ? ` · shortfall ${fmt(capacity.shortfall)}` : ''}
      </div>
    </div>
  )
}

const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3 }
const label: React.CSSProperties = {
  fontSize: 9,
  color: 'var(--t-2)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
}
const input: React.CSSProperties = {
  background: 'var(--bg-2)',
  border: '0.5px solid var(--bd-0)',
  borderRadius: 5,
  padding: '5px 8px',
  color: 'var(--t-0)',
  fontSize: 11,
  minWidth: 90,
}
const inputWarn: React.CSSProperties = { borderColor: '#d8a13a', color: '#f0c674' }
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 6px',
  fontSize: 9,
  textTransform: 'uppercase',
  color: 'var(--t-2)',
  borderBottom: '0.5px solid var(--bd-0)',
}
const td: React.CSSProperties = {
  padding: '6px',
  borderBottom: '0.5px solid var(--bd-0)',
  verticalAlign: 'top',
}
const bannerError: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  fontSize: 11,
  background: 'rgba(220,60,70,.12)',
  border: '1px solid rgba(220,60,70,.4)',
  color: '#ff9aa2',
}
const bannerOk: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  fontSize: 11,
  background: 'rgba(60,180,120,.12)',
  border: '1px solid rgba(60,180,120,.4)',
  color: '#8fe0b6',
}
const btnPrimary: React.CSSProperties = {
  flex: 1,
  fontSize: 11,
  fontWeight: 600,
  padding: '7px 8px',
  borderRadius: 7,
  border: 'none',
  background: 'var(--ok)',
  color: '#fff',
  cursor: 'pointer',
}
const btnDanger: React.CSSProperties = {
  flex: 1,
  fontSize: 11,
  fontWeight: 600,
  padding: '7px 8px',
  borderRadius: 7,
  border: '0.5px solid var(--bd-0)',
  background: 'transparent',
  color: 'var(--t-0)',
  cursor: 'pointer',
}
